import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import HGError from 'components/HGForm/HGError';
import SerialNumberInput from 'components/HGForm/SerialNumberInput';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import DeviceTypeDropDown from './DeviceTypeDropdown';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { logActivity } from 'helper';
import { API_ENDPOINT, useJwtToken } from 'helper';

const AddDeviceForm = ({ setSuccess, setNewDevice }) => {
    const { Site_ID } = useGetIDsFromURL();
    const accessToken = useJwtToken();
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const addDevice = async (deviceInfo) => {
        setIsLoading(true);
        try {
            const newDevices = await addDeviceToSite(
                Site_ID,
                deviceInfo,
                accessToken
            );

            if (newDevices) {
                setNewDevice(newDevices);
                setSuccess(true);
                setErrorMessage(null);
            } else {
                setErrorMessage('Device not added');
            }
            setIsLoading(false);
        } catch (error) {
            setErrorMessage(error.message);
            setIsLoading(false);
        }
    };

    return (
        <Formik
            initialValues={{
                serialNumber: '',
                deviceName: '',
                deviceType: '',
            }}
            validationSchema={Yup.object({
                serialNumber: Yup.string().required('Serial Number required'),
                deviceType: Yup.string().required('Device Type required'),
            })}
            onSubmit={addDevice}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <Form>
                <div className="mb-5 flex flex-col gap-5 md:flex-row md:items-end">
                    <DeviceTypeDropDown />
                    <SerialNumberInput
                        name="serialNumber"
                        type="text"
                        placeholder="Serial Number"
                    />
                </div>
                {errorMessage && <HGError errorMessage={errorMessage} />}
                <div className="w-full md:ml-auto md:w-max">
                    <HGFormSubmitBtn
                        className="w-full md:w-auto"
                        loading={isLoading}
                    >
                        Add Device
                    </HGFormSubmitBtn>
                </div>
            </Form>
        </Formik>
    );
};

const addDeviceToSite = async (Site_ID, deviceInfo, jwtAccessToken) => {
    let Thing_ID = null;
    try {
        //if the device type includes NEP and the SN does not have NEP  in it then append NEP to the SN
        //if the device type includes BG and the SN does not have BG in it then append BG to the SN
        //otherise throw an error
        if (deviceInfo?.deviceType.includes('NEP')) {
            Thing_ID = 'NEP_' + deviceInfo.serialNumber;
        } else if (deviceInfo?.deviceType.includes('Battery')) {
            Thing_ID = 'BG_' + deviceInfo.serialNumber;
        } else if (
            !deviceInfo?.deviceType.includes('NEP') ||
            !deviceInfo?.deviceType.includes('BG')
        ) {
            throw new Error(
                'Invalid Device Type  Device type should be either NEP or Battery System'
            );
        }

        //prevent users from adding the nep or bg to prefix
        if (deviceInfo?.serialNumber.includes('_')) {
            throw new Error(
                'Invalid Serial Number. Serial Number should not contain _'
            );
        }

        if (Site_ID && Thing_ID) {
            const URL = `${API_ENDPOINT}/sites/${Site_ID}/add-device?thingId=${Thing_ID}`;
            const addedDeviceResponse = await fetch(URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: jwtAccessToken,
                },
            });

            if (!addedDeviceResponse.ok) {
                const errorData = await addedDeviceResponse.json();
                throw errorData;
            }
            const data = await addedDeviceResponse.json();
            if (!data) {
                throw new Error('No site data found');
            }
            logActivity(
                'User',
                'Info',
                'Add device to site',
                `Device ${Thing_ID} added to site ${Site_ID} successfully.`,
                {
                    before: null,
                    after: data,
                }
            );
            return data;
        } else {
            throw new Error(
                'Device not discovered yet, please connect device to wifi and try again'
            );
        }
    } catch (error) {
        console.error('Error:', error);
        logActivity(
            'User',
            'Error',
            'Add device to site',
            `Error adding device ${Thing_ID} to site ${Site_ID}. ${error.message}`,
            {
                before: null,
                after: error,
            }
        );
        throw error;
    }
};

export default AddDeviceForm;
